import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash2, Pencil, Camera, AlertTriangle, ShieldCheck } from "lucide-react";
import QuotaBar from "../components/QuotaBar";
import PageHero from "../components/PageHero";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { api } from "../lib/api";
import SafeImage from "../components/SafeImage";
import PhotoEditor from "../components/PhotoEditor";
import RecipeEditorDialog from "../components/RecipeEditorDialog";
import { eur, eurHT, pct, ALLERGENS } from "../lib/format";

/**
 * Recipes — page « Fiches recettes ». Le formulaire d'édition a été extrait
 * dans `<RecipeEditorDialog>` (Feb 2026) afin d'être ré-utilisable depuis la
 * fiche produit → « Recettes à optimiser » sans naviguer vers /plats
 * (consultation contextuelle, cf. cahier des charges).
 */
export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [photoRecipe, setPhotoRecipe] = useState(null);
  // État d'ouverture du sous-dialogue Recette : `open` ; recette éditée : `editing`
  // (objet complet — évite un fetch inutile côté RecipeEditorDialog).
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // recette | null (création)
  const [searchParams, setSearchParams] = useSearchParams();

  const load = async () => {
    const r = await api.recipes();
    setRecipes(r);
    return r;
  };
  useEffect(() => {
    (async () => {
      const rs = await load();
      const editId = searchParams.get("edit");
      if (editId) {
        const target = rs.find((x) => x.id === editId);
        if (target) { setEditing(target); setEditorOpen(true); }
        searchParams.delete("edit");
        setSearchParams(searchParams, { replace: true });
      }
    })();
    const refresh = () => { load().catch(() => {}); };
    const evs = ["rentascan:invoice-validated", "rentascan:product-updated", "rentascan:recipe-updated"];
    evs.forEach((e) => window.addEventListener(e, refresh));
    return () => evs.forEach((e) => window.removeEventListener(e, refresh));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew  = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (r) => { setEditing(r); setEditorOpen(true); };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette fiche recette ?")) return;
    await api.deleteRecipe(id);
    toast.success("Fiche recette supprimée");
    window.dispatchEvent(new CustomEvent("rentascan:usage-changed", { detail: { resource: "recipes" } }));
    await load();
  };

  return (
    <div className="space-y-5">
      <PageHero
        eyebrow="Marges & rentabilité"
        title="Fiches recettes"
        subtitle={`${recipes.length} fiche(s) · coût, rentabilité et objectifs`}
        pattern="dots"
        action={
          <Button data-testid="new-recipe-btn" onClick={openNew} className="btn-orange !h-11 border-0">
            <Plus className="w-4 h-4" /> Nouvelle fiche recette
          </Button>
        }
      />

      <QuotaBar resource="recipes" label="Fiches recettes créées" />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {recipes.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-16 border border-dashed border-[#CDD7E3] rounded-xl bg-white">
            Aucune fiche recette. Créez-en une ou chargez la démo.
          </div>
        )}
        {recipes.map((r) => {
          const c = r.computed || {};
          const under = c.under_target;
          return (
            <Card key={r.id} data-testid={`recipe-card-${r.id}`} className="rounded-xl border-[#CDD7E3] overflow-hidden group hover:shadow-[0_16px_40px_rgba(11,31,51,0.10)] transition-shadow p-0 gap-0">
              <div className="relative h-[220px] bg-[#F4F5EF] overflow-hidden">
                <SafeImage src={r.photo_url} alt={r.name} className="absolute inset-0 w-full h-full group-hover:scale-[1.03] transition-transform duration-500" data-testid={`recipe-photo-img-${r.id}`} />
                <div className="absolute top-3 left-3 bg-white/95 backdrop-blur text-[10.5px] uppercase tracking-widest font-bold text-[#0B1F33] px-2 py-1 rounded">{r.category}</div>
                <div className="absolute top-3 right-3">
                  {under ? <span className="pill pill-warn">Recette à optimiser · obj. {r.target_margin_pct}%</span>
                         : <span className="pill pill-down">Rentabilité saine</span>}
                </div>
                <div className="absolute bottom-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="icon" variant="secondary" onClick={() => setPhotoRecipe(r)} data-testid={`recipe-photo-${r.id}`} className="w-8 h-8 bg-white hover:bg-white/90" title="Atelier photo"><Camera className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="secondary" onClick={() => openEdit(r)} data-testid={`recipe-edit-${r.id}`} className="w-8 h-8 bg-white hover:bg-white/90"><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="secondary" onClick={() => remove(r.id)} data-testid={`recipe-delete-${r.id}`} className="w-8 h-8 bg-white hover:bg-white/90"><Trash2 className="w-3.5 h-3.5 text-red-600" /></Button>
                </div>
              </div>
              <CardContent className="p-5">
                <h3 className="font-heading font-extrabold text-[#0B1F33] text-[19px] tracking-tight leading-tight">{r.name}</h3>
                <div className="mt-4 flex items-end justify-between border-b border-[#CDD7E3] pb-4">
                  <div>
                    <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875]">Rentabilité par portion</div>
                    <div data-testid={`recipe-margin-eur-${r.id}`} className="mt-0.5 text-[30px] font-extrabold text-[#0B1F33] tabular-nums leading-none">{eur(c.matter_margin_eur)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10.5px] uppercase tracking-widest font-bold text-[#5E6875]">Ratio</div>
                    <div data-testid={`recipe-margin-pct-${r.id}`} className={`text-[18px] font-extrabold tabular-nums ${under ? "text-[#D84343]" : "text-[#24845B]"}`}>{pct(c.matter_margin_pct)}</div>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-3 text-[12px]">
                  <div><div className="text-[10px] text-[#5E6875] uppercase tracking-widest font-bold">Prix vente</div><div className="font-mono-num text-[13px] font-bold text-[#0B1F33] mt-0.5">{eurHT(c.selling_price_ht)}</div></div>
                  <div><div className="text-[10px] text-[#5E6875] uppercase tracking-widest font-bold">Coût recette</div><div data-testid={`recipe-food-cost-${r.id}`} className="font-mono-num text-[13px] font-bold text-[#0B1F33] mt-0.5">{eur(c.matter_cost_per_portion, 3)}</div></div>
                  <div><div className="text-[10px] text-[#5E6875] uppercase tracking-widest font-bold">Food-cost</div><div className="font-mono-num text-[13px] font-bold text-[#0B1F33] mt-0.5">{pct(c.food_cost_pct)}</div></div>
                </div>
                <RecipeCardAllergens recipe={r} onEdit={() => openEdit(r)} />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Formulaire recette (édition/création) — dialog extrait, réutilisé par
          la fiche produit → « Recettes à optimiser » pour éviter toute
          navigation vers /plats. */}
      <RecipeEditorDialog
        open={editorOpen}
        onOpenChange={(v) => { setEditorOpen(v); if (!v) setEditing(null); }}
        recipeId={editing?.id || null}
        initialRecipe={editing}
        onSaved={load}
      />

      {photoRecipe && <PhotoEditor recipe={photoRecipe} onClose={() => setPhotoRecipe(null)} onSaved={load} />}
    </div>
  );
}

/**
 * Badge INCO 1169/2011 affiché sur la carte recette.
 *  - Vert : au moins un allergène a été validé manuellement par le chef.
 *  - Orange : des allergènes ont été suggérés depuis les ingrédients mais
 *    n'ont pas encore été confirmés (le chef doit ouvrir la fiche pour valider).
 *  - Gris : aucune donnée (recette sans allergène connu).
 */
function RecipeCardAllergens({ recipe, onEdit }) {
  const validated = recipe.allergens || [];
  const suggested = (recipe.computed?.allergens_suggested) || [];
  const pending = suggested.filter((c) => !validated.includes(c));
  const labelsFor = (codes) => codes.map((c) => ALLERGENS.find((a) => a.code === c)?.label || c).filter(Boolean);

  if (pending.length > 0) {
    return (
      <div className="mt-3 rounded-md border border-[#FFD79E] bg-[#FFF6E5] p-2.5 flex items-center gap-2" data-testid={`recipe-allergens-pending-${recipe.id}`}>
        <AlertTriangle className="w-4 h-4 text-[#7A4B00] shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-[11.5px] font-bold text-[#7A4B00] leading-tight">Allergènes à confirmer ({pending.length})</div>
          <div className="text-[11px] text-[#7A4B00] truncate mt-0.5">{labelsFor(pending).slice(0, 4).join(", ")}{pending.length > 4 ? "…" : ""}</div>
        </div>
        <Button size="sm" onClick={onEdit} className="!h-7 px-2 text-[11px] bg-[#0B1F33] hover:bg-[#1a3050] text-white">Vérifier</Button>
      </div>
    );
  }
  if (validated.length > 0) {
    return (
      <div className="mt-3 rounded-md border border-[#B7DBC6] bg-[#E4F1EB] p-2.5 flex items-center gap-2" data-testid={`recipe-allergens-ok-${recipe.id}`}>
        <ShieldCheck className="w-4 h-4 text-[#2E7D5B] shrink-0" />
        <div className="text-[11px] font-semibold text-[#1B5C39] truncate">
          Allergènes validés : {labelsFor(validated).slice(0, 4).join(", ")}{validated.length > 4 ? "…" : ""}
        </div>
      </div>
    );
  }
  return null;
}
